'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';
import { Menu, X, ArrowRight, Phone } from 'lucide-react';
import Logo from '@/components/Logo';
import { SITE } from '@/lib/site';

const navItems = [
  { href: '/', label: '홈' },
  { href: '/curriculum', label: '커리큘럼' },
  { href: '/enrollment', label: '수강신청' },
  { href: '/contact', label: '문의하기' },
];

export default function Navigation() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const pathname = usePathname();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => setIsOpen(false), [pathname]);

  const active = (href: string) => (href === '/' ? pathname === '/' : pathname?.startsWith(href));

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled || isOpen ? 'border-b border-cream-200 bg-white/85 backdrop-blur-lg' : 'border-b border-transparent bg-white'
      }`}
    >
      <nav className="wrap flex h-[72px] items-center justify-between">
        <Logo />

        <div className="hidden items-center gap-1 rounded-full border border-cream-200 bg-white/70 p-1 md:flex">
          {navItems.map((item) => (
            <Link
              key={item.href}
              href={item.href}
              className={`rounded-full px-5 py-2 text-[15px] font-semibold transition-colors ${
                active(item.href) ? 'bg-forest text-white' : 'text-muted hover:text-ink'
              }`}
            >
              {item.label}
            </Link>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <a href={`tel:${SITE.phone}`} className="hidden items-center gap-2 px-3 text-sm font-bold text-ink lg:flex">
            <Phone className="h-4 w-4 text-forest-600" />
            {SITE.phone}
          </a>
          <Link href="/enrollment" className="btn-coral hidden px-5 py-2.5 text-sm sm:inline-flex">
            무료 상담 신청
          </Link>
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="-mr-2 rounded-full p-2.5 text-ink md:hidden"
            aria-label="메뉴"
          >
            {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </nav>

      {isOpen && (
        <div className="wrap pb-6 md:hidden">
          <div className="overflow-hidden rounded-3xl border border-cream-200 bg-white">
            {navItems.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className={`flex items-center justify-between border-b border-cream-100 px-6 py-4 text-lg font-bold last:border-0 ${
                  active(item.href) ? 'text-forest-600' : 'text-ink'
                }`}
              >
                {item.label}
                <ArrowRight className="h-4 w-4 opacity-40" />
              </Link>
            ))}
          </div>
          <Link href="/enrollment" className="btn-coral mt-3 w-full">
            무료 상담 신청
          </Link>
        </div>
      )}
    </header>
  );
}
